import { useState, useEffect } from "react";
import "../css/UnitList.css";

const SaleHistory = () => {
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}product-list.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setProducts(data.data);
        }
      } catch (error) {
        showToast("Failed to load products", "error");
      }
    };
    fetchProducts();
  }, [API_URL]);

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      // The API doesn't seem to support date ranges, so we fetch all data for a product
      const requestBody = selectedProductId
        ? { product_id: selectedProductId }
        : {};

      const response = await fetch(`${API_URL}stock-sales-history.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      let salesData = [];
      if (data.status && Array.isArray(data.sales)) {
        salesData = data.sales;
      } else if (data.status && Array.isArray(data.data)) {
        // Fallback for purchase data format
        salesData = data.data;
      }

      setHistory(salesData);

      if (salesData.length === 0) {
        showToast(data.message || "No history found.", "info");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [selectedProductId, API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Client-side filtering for the date range
  const filteredHistory = history.filter((item) => {
    // The date from the API is in 'YYYY-MM-DD' format
    const itemDate = new Date(item.purchase_date);
    const isAfterStartDate = !startDate || itemDate >= new Date(startDate);
    const isBeforeEndDate = !endDate || itemDate <= new Date(endDate);
    return isAfterStartDate && isBeforeEndDate;
  });

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Stock & Sales History</h1>
          <p>View all stock movements (purchases and sales)</p>
        </div>
      </div>

      <div
        className="filters-section"
        style={{
          marginBottom: "30px",
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div className="form-group">
          <label htmlFor="product-filter">Filter by Product</label>
          <select
            id="product-filter"
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
          >
            <option value="">All Products</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.product}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="start-date">Start Date</label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>
        <div className="form-group">
          <label htmlFor="end-date">End Date</label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <p>Loading history...</p>
      ) : (
        <div className="unit-list-table-container">
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Purchase Date</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product_name}</td>
                    <td>
                      {item.purchase_date
                        ? new Date(item.purchase_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${item.type.toLowerCase()}`}
                      >
                        {item.type}
                      </span>
                    </td>
                    <td>
                      {item.quantity} {item.unit_name}
                    </td>
                    <td>₹{parseFloat(item.per_unit_price).toFixed(2)}</td>
                    <td>₹{parseFloat(item.total_amt).toFixed(2)}</td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          setItemToDelete(item.id);
                          setShowDeleteModal(true);
                        }}
                        disabled={isDeleting}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    No records found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDeleteModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            style={{
              background: "#fff",
              padding: 24,
              borderRadius: 8,
              minWidth: 300,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this record?</p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 24,
              }}
            >
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                disabled={isDeleting}
                style={{ padding: "6px 16px" }}
              >
                No
              </button>
              <button
                onClick={async () => {
                  if (!itemToDelete) return;
                  setIsDeleting(true);
                  try {
                    const response = await fetch(`${API_URL}delete-sale.php`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ purchase_id: itemToDelete }),
                    });
                    const data = await response.json();
                    if (data.status) {
                      showToast("Deleted successfully", "success");
                      setShowDeleteModal(false);
                      setItemToDelete(null);
                      // Refresh history
                      fetchHistory();
                    } else {
                      showToast(data.message || "Delete failed", "error");
                    }
                  } catch (err) {
                    showToast("Delete failed. Network error.", "error");
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
                style={{
                  background: "red",
                  color: "#fff",
                  padding: "6px 16px",
                  border: "none",
                  borderRadius: 4,
                }}
              >
                {isDeleting ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleHistory;
